/**
 * Assessment Polling Hook
 * Custom hook for polling assessment status with progress updates
 */

import { useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  pollAssessmentStatus,
  fetchAssessmentFeedback,
  setPollingError,
} from "@/store/slices/interviewAssessmentSlice";
import {
  selectAssessmentId,
  selectAssessmentStatus,
  selectPollAttempts,
  selectIsPolling,
} from "@/store/selectors/interviewAssessmentSelectors";
import { POLLING_CONFIG } from "@/types/interview-feedback.types";
import { logger } from "@/utils/logger"

interface UseAssessmentPollingOptions {
  /**
   * Callback when assessment is completed
   */
  onCompleted?: () => void;

  /**
   * Callback when polling fails or times out
   */
  onError?: (error: string) => void;

  /**
   * Callback when polling times out
   */
  onTimeout?: () => void;

  /**
   * Auto-start polling when assessment ID is available
   * @default true
   */
  autoStart?: boolean;

  /**
   * Meeting session UUID (required for polling)
   */
  meetingSession?: string;
}

interface UseAssessmentPollingReturn {
  /**
   * Start polling for assessment status
   */
  startPolling: () => void;

  /**
   * Stop polling
   */
  stopPolling: () => void;

  /**
   * Whether polling is currently active
   */
  isPolling: boolean;

  /**
   * Number of poll attempts made
   */
  pollAttempts: number;

  /**
   * Current assessment status
   */
  status: string | null;
}

/**
 * Hook for polling assessment status with automatic retry and timeout
 */
export const useAssessmentPolling = (
  options: UseAssessmentPollingOptions = {}
): UseAssessmentPollingReturn => {
  const {
    onCompleted,
    onError,
    onTimeout,
    autoStart = true,
    meetingSession,
  } = options;

  const dispatch = useAppDispatch();

  // Redux state
  const assessmentId = useAppSelector(selectAssessmentId);
  const status = useAppSelector(selectAssessmentStatus);
  const pollAttempts = useAppSelector(selectPollAttempts);
  const isPolling = useAppSelector(selectIsPolling);

  // Refs to track polling state
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const isPollingActiveRef = useRef(false);
  const pollAttemptsRef = useRef(pollAttempts);

  // Keep ref up to date to avoid stale closure in intervals
  useEffect(() => {
    pollAttemptsRef.current = pollAttempts;
  }, [pollAttempts]);

  /**
   * Stop polling and cleanup
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingActiveRef.current = false;
    logger.log("[Polling] Stopped");
  }, []);

  /**
   * Poll assessment status once
   */
  const pollOnce = useCallback(async () => {
    if (!assessmentId) {
      logger.warn("[Polling] No assessment ID available");
      return;
    }

    if (!meetingSession) {
      logger.error("[Polling] No meeting session available");
      stopPolling();
      onError?.("Meeting session not found");
      return;
    }

    try {
      const result = await dispatch(
        pollAssessmentStatus({ assessmentId, meetingSession })
      ).unwrap();

      logger.log("[Polling] Status check:", {
        status: result.status,
        attempts: pollAttemptsRef.current + 1,
      });

      // Check if completed
      if (result.status === "completed") {
        logger.log("[Polling] ✅ Assessment completed!");
        stopPolling();

        // Fetch feedback
        try {
          await dispatch(fetchAssessmentFeedback(assessmentId)).unwrap();
          onCompleted?.();
        } catch (error: any) {
          logger.error("[Polling] Failed to fetch feedback:", error);
          onError?.(error.message || "Failed to load feedback");
        }
        return;
      }

      // Check if failed/error
      if (result.status === "error") {
        logger.error("[Polling] ❌ Assessment processing failed");
        stopPolling();
        const errorMsg = "Assessment processing failed";
        dispatch(setPollingError(errorMsg));
        onError?.(errorMsg);
        return;
      }

      // Check timeout
      if (pollAttemptsRef.current + 1 >= POLLING_CONFIG.MAX_ATTEMPTS) {
        logger.warn("[Polling] ⏱️ Timeout reached");
        stopPolling();
        const timeoutMsg = "Assessment processing timeout - check back later";
        dispatch(setPollingError(timeoutMsg));
        onTimeout?.();
        return;
      }
    } catch (error: any) {
      logger.error("[Polling] Error during poll:", error);

      // Don't stop polling on network errors, just log and continue
      // Only stop if we've exceeded max attempts
      if (pollAttemptsRef.current + 1 >= POLLING_CONFIG.MAX_ATTEMPTS) {
        stopPolling();
        onError?.(error.message || "Polling failed");
      }
    }
  }, [
    assessmentId,
    meetingSession,
    dispatch,
    stopPolling,
    onCompleted,
    onError,
    onTimeout,
  ]);

  /**
   * Start polling with interval
   */
  const startPolling = useCallback(() => {
    if (!assessmentId) {
      logger.warn("[Polling] Cannot start - no assessment ID");
      return;
    }

    if (isPollingActiveRef.current) {
      logger.log("[Polling] Already active");
      return;
    }

    logger.log("[Polling] Starting...", {
      assessmentId,
      interval: POLLING_CONFIG.INTERVAL_MS,
      maxAttempts: POLLING_CONFIG.MAX_ATTEMPTS,
    });

    isPollingActiveRef.current = true;

    // First poll immediately
    pollOnce();

    // Then poll at intervals
    pollingIntervalRef.current = setInterval(() => {
      pollOnce();
    }, POLLING_CONFIG.INTERVAL_MS);
  }, [assessmentId, pollOnce]);

  /**
   * Auto-start polling when assessment ID becomes available
   */
  useEffect(() => {
    if (autoStart && assessmentId && !isPollingActiveRef.current) {
      logger.log("[Polling] Auto-starting for assessment:", assessmentId);
      startPolling();
    }
  }, [autoStart, assessmentId, startPolling]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling,
    pollAttempts,
    status,
  };
};
